import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import User from "../../../api/auth.js";
import AuthLayout from "../../../components/organisms/Layout/Authlayout";
import { heyhireBlackIcon } from "../../../assets/images.js";

const SuccessView = ({ resend }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <i className="fas fa-check text-green-600 text-xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Password Reset Successful
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t("login.if-not-redirected")}&nbsp;
          <button
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            onClick={resend}
          >
            {t("login.click-here")}
          </button>
        </p>
      </div>
    </div>
  );
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const token = pathName.split("password-reset/")[1];
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  useEffect(() => {
    document.title = t("title.heyhire-portal-reset-password");
    return () => {
      setPassword("");
      setConfirmPassword("");
      setLoading(false);
      setValidationErrors(null);
    };
  }, []);

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;
    setPasswordMatchError(false);
    if (name === "password") {
      setPassword(value);
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setValidationErrors(null);
    if (password === "" || password !== confirmPassword) {
      setPasswordMatchError(true);
    } else {
      setLoading(true);
      const body = {
        //email: _email,
        token: token,
        password: password,
        password_confirmation: confirmPassword,
      };
      User.ResetPasswordChange(body)
        .then(() => {
          localStorage.removeItem("passwordResetEmail");
          toast.success(t("login.password-changed"));
          navigate(`/login`);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          toast.error(t("common.something-went-wrong-please-try-again"));
          console.error(error);
        });
    }
  };

  return (
    <AuthLayout
      showLink={false}
      title={
        completed ? t("login.password-redirecting") : t("login.reset-password")
      }
    >
      <div className="flex min-h-screen flex-1 flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {!completed ? (
            <>
              {/* Logo and Title */}
              <div className="text-center mb-8">
                <img
                  className="mx-auto h-12 w-auto"
                  src={heyhireBlackIcon}
                  alt="HeyHire"
                />
                <h2 className="mt-8 text-center text-3xl font-bold tracking-tight text-gray-900">
                  {t("login.reset-password")}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  Enter your new password below
                </p>
              </div>

              {/* Form Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
                <form className="space-y-6" onSubmit={handleLogin}>
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {t("general-settings.new-password")}
                    </label>
                    <div className="relative">
                      <input
                        className={`block w-full rounded-lg border ${validationErrors?.password || passwordMatchError
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                          } px-4 py-2.5 pr-10 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 sm:text-sm transition-colors`}
                        name="password"
                        type={passwordVisible ? "text" : "password"}
                        value={password}
                        placeholder="Enter new password"
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                      >
                        <i
                          className={`far ${passwordVisible ? "fa-eye-slash" : "fa-eye"
                            } text-sm`}
                        />
                      </button>
                    </div>
                    {validationErrors?.password && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.password[0]}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {t("common.confirmPassword")}
                    </label>
                    <div className="relative">
                      <input
                        className={`block w-full rounded-lg border ${passwordMatchError || validationErrors?.confirmPassword
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                          } px-4 py-2.5 pr-10 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 sm:text-sm transition-colors`}
                        name="confirmPassword"
                        type={confirmPasswordVisible ? "text" : "password"}
                        value={confirmPassword}
                        placeholder="Confirm new password"
                        onChange={handleInputChange}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                      >
                        <i
                          className={`far ${confirmPasswordVisible ? "fa-eye-slash" : "fa-eye"
                            } text-sm`}
                        />
                      </button>
                    </div>
                    {passwordMatchError && (
                      <p className="mt-2 text-sm text-red-600">
                        {t("errors.password-not-match")}
                      </p>
                    )}
                    {validationErrors?.confirmPassword && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.confirmPassword[0]}
                      </p>
                    )}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex w-full justify-center items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <i className="fad fa-circle-notch fa-spin mr-2" />
                          {t("common.continue")}...
                        </>
                      ) : (
                        t("common.continue")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <SuccessView resend={() => setCompleted(false)} />
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
